import { FileDownload } from '@mui/icons-material';
import { IconButton, ListSubheader, Menu, MenuItem } from '@mui/material';
import {
  GridExcelExportOptions,
  gridFilteredSortedRowIdsSelector,
} from '@mui/x-data-grid-premium';
import { GridApiPremium } from '@mui/x-data-grid-premium/models/gridApiPremium';
import { MouseEvent, MutableRefObject, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { usePortalLinkContext } from '~/components/PortalLink';
import { cookieEnabledTestFeature, getExportFileName } from '~/utils/helper';

export enum ReportExportOptions {
  Excel = 'excel',
  CSV = 'csv',
}

type ExportOptionMenuItemProps = {
  type: ReportExportOptions;
  label: string;
  options: GridExcelExportOptions;
};

const ExportOptionMenuItem = (
  exportProps: ExportOptionMenuItemProps & {
    apiRef: MutableRefObject<GridApiPremium>;
  },
) => {
  const { apiRef, type, label, options } = exportProps;
  return (
    <MenuItem
      key={`export-${type}`}
      onClick={() => {
        switch (type) {
          case ReportExportOptions.Excel:
            return apiRef.current.exportDataAsExcel(options);
          case ReportExportOptions.CSV:
            return apiRef.current.exportDataAsCsv(options);
          default:
            return;
        }
      }}
    >
      {label}
    </MenuItem>
  );
};

type PropsType = {
  title: string;
  startDate?: Date;
  endDate?: Date;
  apiRef: MutableRefObject<GridApiPremium>;
  exportOptions: ReportExportOptions[];
};

export const ReportDataExport = (props: PropsType) => {
  const { title, apiRef, startDate, endDate, exportOptions } = props;

  const { t } = useTranslation();

  const { isRouteLinksEnabled } = usePortalLinkContext();
  const { enableExcelExport } = useMemo(() => {
    // Excel Exporting has some issues when used in Library Component on Legacy UI side.
    // Disabled the Excel Export on Legacy UI, except with test cookie present, until issue has been fixed.
    const isLibraryComponentInLegacyUI = !isRouteLinksEnabled;

    return {
      enableExcelExport: isLibraryComponentInLegacyUI
        ? cookieEnabledTestFeature('dataGridExcelExport')
        : true,
    };
  }, [isRouteLinksEnabled]);

  const { menuOptions } = useMemo(() => {
    const enabledExportOptions = enableExcelExport
      ? exportOptions
      : exportOptions.filter((o) => o !== ReportExportOptions.Excel);

    const fileName = getExportFileName({
      title,
      startDate,
      endDate,
    });

    const menuOptions: ExportOptionMenuItemProps[] = [
      {
        type: ReportExportOptions.Excel,
        label: t('laExpExcel'),
        options: {
          fileName,
        },
      },
      {
        type: ReportExportOptions.CSV,
        label: t('laExpCSV'),
        options: {
          fileName,
          delimiter: ';',
          utf8WithBom: true,
        },
      },
    ].filter(({ type }) => enabledExportOptions.includes(type));

    return {
      menuOptions,
    };
  }, [title, startDate, endDate, t, exportOptions, enableExcelExport]);

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const handleClick = (event: MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  const selectedRowsCount = !!apiRef.current.getSelectedRows
    ? apiRef?.current?.getSelectedRows()?.size || 0
    : 0;
  const hasSelectedRows = selectedRowsCount > 0;

  const filteredRowsCount = !!apiRef.current.state
    ? gridFilteredSortedRowIdsSelector(apiRef)?.length
    : 0;

  return (
    <>
      <IconButton
        id={'report-export-button'}
        aria-controls={open ? 'basic-menu' : undefined}
        aria-haspopup={'true'}
        aria-expanded={open ? 'true' : undefined}
        disabled={exportOptions.length < 1}
        onClick={handleClick}
      >
        <FileDownload />
      </IconButton>
      <Menu
        id={'report-export-menu'}
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        MenuListProps={{
          'aria-labelledby': 'report-export-button',
        }}
      >
        {hasSelectedRows && (
          <ListSubheader sx={{ lineHeight: '2em' }}>{`${t(
            'selected',
          )} (${selectedRowsCount})`}</ListSubheader>
        )}
        {menuOptions.map((exportProps, index) => {
          return (
            <ExportOptionMenuItem
              apiRef={apiRef}
              {...exportProps}
              key={`export-option-${index}`}
            />
          );
        })}
        {hasSelectedRows && (
          <ListSubheader sx={{ lineHeight: '2em' }}>{`${t(
            'all',
          )} (${filteredRowsCount})`}</ListSubheader>
        )}
        {hasSelectedRows &&
          menuOptions.map((exportProps, index) => {
            const exportOptions: GridExcelExportOptions = {
              ...exportProps.options,
              getRowsToExport: (params) => {
                return gridFilteredSortedRowIdsSelector(params.apiRef);
              },
            };

            return (
              <ExportOptionMenuItem
                key={`export-all-option-${index}`}
                apiRef={apiRef}
                {...exportProps}
                options={exportOptions}
              />
            );
          })}
      </Menu>
    </>
  );
};
